import { API } from '../../utils/amplifyWrapper';
import { useQuery } from "@tanstack/react-query";

// Feature keys understood by the billing-service estimator.
export type FeatureKey =
    | 'dubbing'
    | 'voice-changer'
    | 'voice-isolation'
    | 'voice-design'
    | 'voice-clone'
    | 'voice-mix';

export interface IFeatureCreditEstimate {
    credits: number;              // estimated cost (0 until real rates are configured)
    credits_remaining?: number;   // balance left (present when priced against an org)
    can_create?: boolean;         // whether the balance covers the cost
}

export const fetchFeatureCreditEstimate = async (
    org_id: string,
    feature: FeatureKey,
    duration_seconds?: number | null
): Promise<IFeatureCreditEstimate> => {
    const response = await API.post('main', `/organisation/${org_id}/feature-credits/estimate`, {
        body: { feature, duration_seconds: duration_seconds ?? null },
        headers: { 'Content-Type': 'application/json' },
    });
    return response.data as IFeatureCreditEstimate;
};

// Live cost estimate for a feature the user is about to run — used to show "this will cost N
// credits" and to disable submit when the balance is short. Duration-priced features
// (dubbing/changer/isolation) should pass duration_seconds; flat features can omit it.
export const useFeatureCreditEstimate = ({
    org_id,
    feature,
    duration_seconds,
    enabled = true,
}: {
    org_id: string;
    feature: FeatureKey;
    duration_seconds?: number | null;
    enabled?: boolean;
}) => {
    return useQuery(
        ['feature_credit_estimate', org_id, feature, duration_seconds ?? null],
        () => fetchFeatureCreditEstimate(org_id, feature, duration_seconds),
        { enabled: !!(org_id && feature && enabled) }
    );
};

// Gates a submit button on the same estimate `FeatureCreditCost` renders, so the "not enough
// credits" message and the disabled state can never drift apart. `enabled` should mirror the
// form's own submit-readiness check (only worth pricing once the form could otherwise submit).
export const useFeatureCreditGate = ({
    org_id,
    feature,
    duration_seconds,
    enabled = true,
}: {
    org_id: string;
    feature: FeatureKey;
    duration_seconds?: number | null;
    enabled?: boolean;
}) => {
    const { data } = useFeatureCreditEstimate({ org_id, feature, duration_seconds, enabled });
    return { insufficientCredits: data?.can_create === false };
};
